import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, ChevronLeft, ChevronRight, Eye, Users } from 'lucide-react';
import ResultsClient from '../data/api/ResultsClient';
import Loader from '../../../shared/components/Loader';
import { CustomDropdown } from '../../../shared/components/CustomDropdown';
import '../styles/ResultsStyles.css';

const LearnerResultsPage: React.FC = () => {
  const location = useLocation();
  const scheduleId = location.state?.scheduleId;
  const navigate = useNavigate();

  const [learners, setLearners] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Pagination
  const [page, setPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const limit = 10;

  useEffect(() => {
    fetchLearners();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter]);

  const fetchLearners = async () => {
    try {
      setLoading(true);
      const params: any = {
        page_number: page,
        limit,
        session_id: scheduleId,
      };

      if (searchTerm) {
        if (searchTerm.includes('@')) {
          params.email_address = searchTerm;
        } else {
          params.name = searchTerm;
        }
      }

      if (statusFilter) {
        params.status = statusFilter;
      }

      const response = await ResultsClient.getScheduleUsers(params);
      setLearners(response?.user_list || response?.data || response?.users || []);
      setTotalRecords(response?.total_records || response?.data?.length || response?.user_list?.length || 0);
    } catch (error) {
      console.error("Error fetching learners:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      setPage(1);
      fetchLearners();
    }
  };

  const handleViewFeedback = (sessionId: string, candidateId: string) => {
    if (sessionId) {
      navigate(`/dashboard/results/stats`, { state: { scheduleId, sessionId, candidateId } });
    }
  };

  const totalPages = Math.ceil(totalRecords / limit) || 1;

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'IN PROGRESS', label: 'In Progress' },
    { value: 'COMPLETED', label: 'Completed' },
    { value: 'SCORED', label: 'Scored' },
    { value: 'FAILED', label: 'Failed' },
    { value: 'NOT ATTENDED', label: 'Not Attended' }
  ];

  return (
    <motion.div className="learner-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.25 }}>
      <Loader isLoading={loading} message="Loading applicants..." />

      {/* ── Hero ── */}
      <div className="lp-hero" style={{ flexDirection: 'column', gap: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', width: '100%' }}>
          <button
            onClick={() => navigate('/dashboard/interviews')}
            style={{
              background: '#fff',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              padding: '8px',
              cursor: 'pointer',
              color: '#64748b',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
            }}
          >
            <ChevronLeft size={20} />
          </button>
          <div className="lp-hero-left">
            <h1>Attended Learners</h1>
            <span className="lp-hero-chip" title="Schedule Information" style={{
              background: '#ecfdf5',
              border: '1px solid #a7f3d0',
              color: '#059669',
              padding: '6px 16px',
              borderRadius: '24px',
              fontWeight: 600,
              fontSize: '0.85rem'
            }}>
              {learners.length > 0 && learners[0].session_name
                ? `${learners[0].session_name} • ${learners[0].round_type || ''}`
                : 'Review detailed performance metrics and feedback for each candidate.'}
            </span>
          </div>
        </div>
      </div>

      {/* ── Table Panel ── */}
      <div className="lp-table-panel" style={{ marginTop: '16px' }}>
        {/* Toolbar */}
        <div className="lp-table-panel-head" style={{ padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', flexWrap: 'wrap', gap: '16px', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '8px 16px', gap: '12px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
              <div style={{ width: 32, height: 32, borderRadius: '8px', background: 'rgba(45, 190, 96, 0.1)', color: 'var(--green-400)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Users size={16} strokeWidth={2.5} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ fontSize: '1rem', color: '#64748b', fontWeight: 600, textTransform: 'none', letterSpacing: '0.5px' }}>Total Applicants:</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#0f172a', lineHeight: 1, letterSpacing: '-0.5px' }}>{totalRecords}</div>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            {/* Search */}
            <div className="lp-search" style={{ height: 40, width: 260, background: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', position: 'relative' }}>
              <Search size={16} color="#94a3b8" />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                onKeyDown={handleSearch}
                style={{ paddingRight: searchTerm ? '30px' : '10px' }}
              />
              {searchTerm && (
                <button
                  style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', border: 'none', background: '#f1f5f9', cursor: 'pointer', color: '#64748b', width: '20px', height: '20px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
                  onClick={() => { setSearchTerm(''); fetchLearners(); }}
                >
                  <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              )}
            </div>

            <div style={{ minWidth: '150px' }}>
              <CustomDropdown
                options={statusOptions}
                value={statusFilter}
                onChange={(val) => { setStatusFilter(val); setPage(1); }}
                placeholder="All Statuses"
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="lp-table-scroll-area" style={{ maxHeight: '600px' }}>
          {learners.length === 0 && !loading ? (
            <div className="is-empty">
              <Users size={48} color="#cbd5e1" style={{ margin: '0 auto' }} />
              <h3>No Learners Found</h3>
              <p>There are no learners matching your criteria.</p>
            </div>
          ) : (
            <table className="learner-table">
              <thead>
                <tr>
                  <th>Applicant Name</th>
                  <th>Email Address</th>
                  <th>Score</th>
                  <th>Status</th>
                  <th>Start Time</th>
                  <th>Completion Time</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {learners.map((learner: any, index: number) => {
                  const isScored = learner.status?.toUpperCase() === 'SCORED' || (learner.score !== undefined && learner.score !== null);
                  const fullName = learner.first_name || learner.last_name
                    ? `${learner.first_name || ''} ${learner.last_name || ''}`.trim()
                    : (learner.name || 'Unknown Candidate');

                  const formatDate = (dateStr: string) => {
                    if (!dateStr) return '-';
                    try {
                      const d = new Date(dateStr);
                      if (isNaN(d.getTime())) return dateStr;
                      return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
                    } catch {
                      return dateStr;
                    }
                  };

                  const formatStatus = (s: string) => s.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');

                  const statusValue = learner.status || (isScored ? 'SCORED' : 'INITIATED');
                  const getStatusStyle = (s: string) => {
                    const upper = s.toUpperCase();
                    if (upper === 'SCORED' || upper === 'COMPLETED') return { bg: 'rgba(45, 190, 96, 0.1)', color: 'var(--green-400)' };
                    if (upper === 'FAILED') return { bg: 'rgba(239, 68, 68, 0.1)', color: '#ef4444' };
                    if (upper === 'IN PROGRESS') return { bg: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6' };
                    return { bg: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b' }; // INITIATED or pending
                  };
                  const badgeStyle = getStatusStyle(statusValue);

                  return (
                    <tr key={learner.candidate_id || index}>
                      <td>
                        <strong style={{ color: '#0f172a' }}>{fullName}</strong>
                      </td>
                      <td>
                        <span style={{ color: '#64748b' }}>{learner.email_address || '-'}</span>
                      </td>
                      <td>
                        {isScored && learner.score !== undefined ? (
                          <strong style={{ color: '#0f172a' }}>{learner.score}</strong>
                        ) : (
                          <span style={{ color: '#94a3b8' }}>-</span>
                        )}
                      </td>
                      <td>
                        <span style={{
                          display: 'inline-block',
                          padding: '2px 10px',
                          borderRadius: '999px',
                          fontSize: '0.78rem',
                          fontWeight: 600,
                          background: badgeStyle.bg,
                          color: badgeStyle.color
                        }}>
                          {formatStatus(statusValue)}
                        </span>
                      </td>
                      <td>
                        <span style={{ color: '#64748b', fontSize: '0.9rem' }}>{statusValue === 'NOT ATTENDED' ? '-' : formatDate(learner.created_at)}</span>
                      </td>
                      <td>
                        <span style={{ color: '#64748b', fontSize: '0.9rem' }}>{statusValue === 'NOT ATTENDED' ? '-' : formatDate(learner.submitted_at)}</span>
                      </td>
                      <td>
                        <div className="actions-row">
                          {isScored && learner.session_id && (
                            <button
                              className="action-btn view"
                              title="View Feedback"
                              onClick={() => handleViewFeedback(learner.session_id, learner.candidate_id)}
                            >
                              <Eye size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {learners.length > 0 && (
          <div className="pagination-controls" style={{ padding: '16px 24px', borderTop: '1px solid #e2e8f0', background: '#f8fafc', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '0.85rem', color: '#64748b' }}>
              Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, totalRecords)} of {totalRecords} entries
            </span>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                className="page-btn"
                disabled={page === 1}
                onClick={() => setPage(p => p - 1)}
                style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '6px', cursor: page === 1 ? 'not-allowed' : 'pointer', color: page === 1 ? '#cbd5e1' : '#64748b' }}
              >
                <ChevronLeft size={16} />
              </button>
              <button
                className="page-btn"
                disabled={page >= totalPages}
                onClick={() => setPage(p => p + 1)}
                style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '6px', cursor: page >= totalPages ? 'not-allowed' : 'pointer', color: page >= totalPages ? '#cbd5e1' : '#64748b' }}
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default LearnerResultsPage;
